import {lighten} from '@material-ui/core/styles/colorManipulator';
import {createEpicTheme} from '@epic-ui/theme/dist/createEpicTheme';
import {TypeBackground, TypeText} from '@material-ui/core/styles/createPalette';
import {Overrides} from '@material-ui/core/styles/overrides';
import {breakpoints, ueAccentColors, ueCommonTheme, ueFonts} from '../ueCommonTheme';
import {ThemeOptions} from '@material-ui/core/styles';
import {UeThemeOptions, UeTheme} from '../ueTheme.types';
import {UnrealEngineCustomThemeColors} from '../ueCustom.types';

const darkColors = {
    background: {
        default: '#111111',
        paper: '#262626'
    },
    text: {
        primary: 'rgb(255, 255, 255)',
        secondary: 'rgba(255, 255, 255, 0.6)',
        disabled: 'rgba(255, 255, 255, 0.4)',
        hint: 'rgba(255, 255, 255, 0.5)'
    }
};

const fontSize = 16;
const iconFontSize = 24;

export const classicDarkThemeConfig: ThemeOptions & UeThemeOptions = {
    ...ueCommonTheme,
    palette: {
        ...ueCommonTheme.palette,
        type: 'dark',
        background: {
            default: darkColors.background.default,
            defaultShade: (number: number): string => {
                return lighten(darkColors.background.default, number);
            },
            paper: darkColors.background.paper
        } as Partial<TypeBackground>,
        text: {
            ...darkColors.text
        } as Partial<TypeText>,
        misc: {
            outline: ueCommonTheme.palette.neutral.pearl
        },
        accent: {
            ...ueCommonTheme.palette.accent,
            ...ueAccentColors.bright
        }
    },
    breakpoints: {
        values: {
            ...breakpoints
        }
    },
    overrides: {
        MuiAutocomplete: {
            root: {
                fontSize
            },
            paper: {
                'background-color': 'black',
                border: '1px solid #ffffff7a',
                fontSize
            },
            groupLabel: {
                'background-color': 'black',
                fontSize
            }
        },
        MuiIconButton: {
            label: {
                fontSize: iconFontSize
            }
        },
        MuiFormHelperText: {
            root: {
                fontSize
            }
        },
        MuiFormLabel: {
            root: {
                fontSize
            }
        },
        MuiInputBase: {
            input: {
                fontSize
            }
        },
        MuiMenuItem: {
            root: {
                color: '#fff'
            }
        },
        typography: {
            fontSize
        }
    } as Overrides,
    name: 'ueClassicDark',
    ue: {
        name: 'ueClassicDark',
        colors: {
            buttons: {
                primary: {
                    background: {
                        default: '#0aaff1',
                        hover: '#008dc5',
                        disabled: '#008dc5'
                    },
                    border: {
                        default: '#0aaff1',
                        hover: '#008dc5',
                        disabled: '#008dc5'
                    },
                    text: {
                        default: '#fff',
                        hover: '#fff',
                        disabled: '#ccc'
                    }
                },
                secondary: {
                    background: {
                        default: 'rgba(17,17,17,.1)',
                        hover: '#0aaff1',
                        disabled: ''
                    },
                    border: {
                        default: '#0aaff1',
                        hover: '#0aaff1',
                        disabled: '#3d3d3d'
                    },
                    text: {
                        default: '#fff',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    }
                },
                tertiary: {
                    background: {
                        default: 'rgba(0,0,0,.5)',
                        hover: 'rgba(0,0,0,.5)',
                        disabled: ''
                    },
                    border: {
                        default: '#757575',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    },
                    text: {
                        default: '#cecece',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    }
                },
                black: {
                    background: {
                        default: '#000',
                        hover: 'rgba(0,0,0,.8)',
                        disabled: ''
                    },
                    border: {
                        default: '#000',
                        hover: '#rgba(0,0,0,.8)',
                        disabled: '#3d3d3d'
                    },
                    text: {
                        default: '#fff',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    }
                },
                circular: {
                    background: {
                        default: 'rgba(0,0,0,.5)',
                        hover: 'rgba(0,0,0,.5)',
                        disabled: ''
                    },
                    border: {
                        default: '#cecece',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    },
                    text: {
                        default: '#cecece',
                        hover: '#fff',
                        disabled: '#3d3d3d'
                    }
                },
                link: {
                    default: '#0aaff1',
                    hover: '#088bc0'
                },
                twinmotion: {
                    default: '#f73476',
                    hover: '#f96597'
                }
            },
            cards: {
                default: {
                    background: '#232323'
                },
                inverted: {
                    background: '#f5f5f5'
                },
                overlay: {
                    background: 'rgba(0,0,0,.35)'
                }
            },
            primary: '#0aaff1'
        } as UnrealEngineCustomThemeColors,
        fonts: ueFonts
    }
};

export const ueClassicDarkTheme = createEpicTheme(classicDarkThemeConfig) as UeTheme;
